import React from 'react';
import { Box, Flex, Text, Skeleton, SkeletonText } from '@chakra-ui/react';

// ─── Design Tokens (DESIGN.md) ────────────────────────────────────────────────
export const BRAND_BLUE  = '#1b73e8';
export const CANVAS_BG   = '#f4f1ec';
export const CARD_BG     = '#ffffff';
export const CARD_SHADOW = '0 1px 4px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)';
export const BORDER      = '#e8e4de';
export const TEXT_DARK   = '#1a202c';
export const TEXT_MUTED  = '#718096';

/**
 * Shared page header shell for all table-list pages.
 * Keeps the warm sand background + white card wrapping consistent.
 *
 * Props:
 *   title      — Page heading string
 *   subtitle   — Optional sub-text below title
 *   actions    — Right-side action buttons/elements (ReactNode)
 *   filters    — Optional filter bar content (ReactNode)
 *   children   — The table content
 */
export default function TablePageShell({ title, subtitle, actions, filters, children }) {
    return (
        <Box fontFamily="heading" pb={8}>

            {/* ── Page Header ── */}
            <Flex
                justifyContent="space-between"
                alignItems={{ base: 'flex-start', sm: 'flex-end' }}
                flexDirection={{ base: 'column', sm: 'row' }}
                gap={3}
                mb={filters ? 5 : 6}
            >
                <Box>
                    <Text
                        fontSize={{ base: '22px', md: '26px' }}
                        fontWeight={800}
                        color={TEXT_DARK}
                        letterSpacing="-0.02em"
                        lineHeight={1}
                        mb={subtitle ? '4px' : 0}
                    >
                        {title}
                    </Text>
                    {subtitle && (
                        <Text fontSize="13px" color={TEXT_MUTED}>
                            {subtitle}
                        </Text>
                    )}
                </Box>

                {actions && (
                    <Box flexShrink={0}>{actions}</Box>
                )}
            </Flex>

            {/* ── Filter Bar ── */}
            {filters && (
                <Box
                    bg={CARD_BG}
                    border={`1px solid ${BORDER}`}
                    borderRadius="16px"
                    boxShadow={CARD_SHADOW}
                    p={4}
                    mb={5}
                >
                    <Flex flexWrap="wrap" gap={3} alignItems="flex-end">
                        {filters}
                    </Flex>
                </Box>
            )}

            {/* ── Table Card ── */}
            <Box
                bg={CARD_BG}
                border={`1px solid ${BORDER}`}
                borderRadius="16px"
                boxShadow={CARD_SHADOW}
                overflow="hidden"
            >
                {children}
            </Box>
        </Box>
    );
}

/**
 * Full-page skeleton loader used as the initial loading state for table pages.
 * Renders a realistic skeleton of the page header + filter bar + table rows.
 */
export function SkeletonTableLoader({ showFilters = true, rows = 8 }) {
    return (
        <Box fontFamily="heading" pb={8}>
            {/* Header skeleton */}
            <Flex justifyContent="space-between" alignItems="center" mb={5}>
                <Box>
                    <Skeleton h="28px" w="180px" borderRadius="8px" mb={2} startColor="#e8e4de" endColor="#f4f1ec" />
                    <Skeleton h="14px" w="260px" borderRadius="6px" startColor="#e8e4de" endColor="#f4f1ec" />
                </Box>
                <Skeleton h="38px" w="120px" borderRadius="10px" startColor="#e8e4de" endColor="#f4f1ec" />
            </Flex>

            {/* Filter bar skeleton */}
            {showFilters && (
                <Box
                    bg={CARD_BG}
                    border={`1px solid ${BORDER}`}
                    borderRadius="16px"
                    boxShadow={CARD_SHADOW}
                    p={4}
                    mb={5}
                >
                    <Flex gap={3} flexWrap="wrap">
                        <Skeleton h="38px" w="220px" borderRadius="8px" startColor="#e8e4de" endColor="#f4f1ec" />
                        <Skeleton h="38px" w="160px" borderRadius="8px" startColor="#e8e4de" endColor="#f4f1ec" />
                        <Skeleton h="38px" w="160px" borderRadius="8px" startColor="#e8e4de" endColor="#f4f1ec" />
                    </Flex>
                </Box>
            )}

            {/* Table card skeleton */}
            <Box
                bg={CARD_BG}
                border={`1px solid ${BORDER}`}
                borderRadius="16px"
                boxShadow={CARD_SHADOW}
                overflow="hidden"
            >
                {/* Table header row */}
                <Box
                    bg="#f7f5f1"
                    px={5}
                    py={3}
                    borderBottom={`1px solid ${BORDER}`}
                >
                    <Flex gap={4}>
                        {[40, 18, 14, 14, 14].map((w, i) => (
                            <Skeleton key={i} h="12px" w={`${w}%`} borderRadius="4px" startColor="#e8e4de" endColor="#f4f1ec" />
                        ))}
                    </Flex>
                </Box>

                {/* Table body rows */}
                {Array.from({ length: rows }).map((_, i) => (
                    <Box
                        key={i}
                        px={5}
                        py={3}
                        borderBottom={i < rows - 1 ? `1px solid ${BORDER}` : 'none'}
                        _hover={{ bg: '#faf9f7' }}
                        style={{
                            animation: `fadeInRow 0.3s ease ${i * 0.04}s both`,
                        }}
                    >
                        <Flex gap={4} alignItems="center">
                            <Skeleton h="36px" w="36px" borderRadius="50%" startColor="#e8e4de" endColor="#f4f1ec" flexShrink={0} />
                            <Flex gap={4} flex={1}>
                                {[35, 15, 15, 15, 20].map((w, j) => (
                                    <Skeleton key={j} h="12px" w={`${w}%`} borderRadius="4px" startColor="#e8e4de" endColor="#f4f1ec" />
                                ))}
                            </Flex>
                        </Flex>
                    </Box>
                ))}
            </Box>

            <style>{`
                @keyframes fadeInRow {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </Box>
    );
}

/**
 * Shared "Add" / action button styled to DESIGN.md brand blue.
 * @param {{ children: React.ReactNode, onClick?: () => void, leftIcon?: React.ReactNode, [key: string]: any }} props
 */
export function BrandButton({ children, onClick, leftIcon, ...rest }) {
    return (
        <Box
            as="button"
            display="inline-flex"
            alignItems="center"
            gap="7px"
            px="16px"
            py="9px"
            borderRadius="10px"
            bg={BRAND_BLUE}
            color="white"
            fontSize="13px"
            fontWeight={700}
            cursor="pointer"
            onClick={onClick}
            _hover={{ bg: '#1558b0', transform: 'translateY(-1px)' }}
            _active={{ transform: 'translateY(0)' }}
            transition="all 0.18s ease"
            boxShadow="0 2px 8px rgba(27,115,232,0.30)"
            whiteSpace="nowrap"
            {...rest}
        >
            {leftIcon && <Box display="flex" alignItems="center">{leftIcon}</Box>}
            {children}
        </Box>
    );
}

/**
 * Outline ghost button (for "Change Filters", "Refresh", etc.)
 * @param {{ children: React.ReactNode, onClick?: () => void, leftIcon?: React.ReactNode, colorScheme?: string, [key: string]: any }} props
 */
export function GhostButton({ children, onClick, leftIcon, colorScheme = 'blue', ...rest }) {
    const colors = {
        blue:   { text: BRAND_BLUE, border: BRAND_BLUE,   hoverBg: '#eff6ff' },
        orange: { text: '#d97706',  border: '#d97706',    hoverBg: '#fffbeb' },
        gray:   { text: '#4a5568',  border: '#cbd5e0',    hoverBg: '#f7fafc' },
    };
    const c = colors[colorScheme] || colors.blue;
    return (
        <Box
            as="button"
            display="inline-flex"
            alignItems="center"
            gap="6px"
            px="14px"
            py="8px"
            borderRadius="10px"
            bg={CARD_BG}
            color={c.text}
            border={`1px solid ${c.border}`}
            fontSize="13px"
            fontWeight={600}
            cursor="pointer"
            onClick={onClick}
            _hover={{ bg: c.hoverBg }}
            transition="all 0.18s ease"
            {...rest}
        >
            {leftIcon && <Box display="flex" alignItems="center">{leftIcon}</Box>}
            {children}
        </Box>
    );
}
